/**
 * Updates headings marked with data-size-guide-dynamic-title (e.g. rich-text-plus).
 * @param {string} title
 */
function syncSizeGuideDynamicTitle(title) {
  const text = String(title || '').trim();
  if (!text) return;

  document.querySelectorAll('[data-size-guide-dynamic-title]').forEach((el) => {
    el.textContent = text;
  });
}

/**
 * @param {string} category
 * @param {string} [title]
 * @param {'service-page-tabs' | 'custom-size-carousel'} [source]
 */
function dispatchSizeGuideCategoryChange(category, title, source) {
  document.dispatchEvent(
    new CustomEvent('size-guide:category-change', {
      detail: {
        category: String(category || '').trim().toLowerCase(),
        title: String(title || '').trim(),
        source: source || '',
      },
    })
  );
}

/**
 * @returns {HTMLElement | null}
 */
function getActiveServicePageTab() {
  const tabsRoot = document.querySelector('service-page-tabs');
  if (!tabsRoot) return null;

  return (
    tabsRoot.querySelector('button[role="tab"][aria-selected="true"]') ||
    tabsRoot.querySelector('button[data-category][data-sp-panel-key]')
  );
}

/**
 * Syncs custom-size-table (and dynamic title) from the active service tab.
 */
function syncFromActiveServiceTab() {
  const tab = getActiveServicePageTab();
  if (!tab) return;

  const category = tab.dataset.category || tab.dataset.sgCategory || '';
  const title = tab.dataset.title || tab.dataset.sgTitle || '';

  if (!category) return;

  dispatchSizeGuideCategoryChange(category, title, 'service-page-tabs');
  syncSizeGuideDynamicTitle(title);
}

/**
 * Runs after custom-size-table and related sections have connected.
 */
function scheduleInitialServiceTabSync() {
  queueMicrotask(() => {
    requestAnimationFrame(syncFromActiveServiceTab);
  });
}

/**
 * Service page tabs: tablist switches panels; category = source_key syncs the size table.
 */
class ServicePageTabs extends HTMLElement {
  constructor() {
    super();

    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleTabKeydown = this.handleTabKeydown.bind(this);
    this.handleCategoryChange = this.handleCategoryChange.bind(this);
    this.handleGroupClick = this.handleGroupClick.bind(this);
    this.handleGroupKeydown = this.handleGroupKeydown.bind(this);

    if (this.hasAttribute('data-navigation')) {
      this.handleNavigation();
    }

    /** @type {{ key: string }} */
    this.state = { key: '' };
  }

  connectedCallback() {
    if (this.initialized) {
      return;
    }

    if (this.dataset.tabMode === 'groups') {
      this.initGroupsMode();
      return;
    }

    this.tabs = [...this.querySelectorAll('button[data-sp-panel-key][role="tab"]')];
    this.panels = [...this.querySelectorAll('[data-sp-panel][data-sp-panel-key]')];

    if (!this.tabs.length) {
      return;
    }

    this.addEventListener('click', this.handleTabClick);
    this.addEventListener('keydown', this.handleTabKeydown);
    document.addEventListener('size-guide:category-change', this.handleCategoryChange);

    const initialKey =
      this.dataset.initialPanelKey || this.tabs[0]?.getAttribute('data-sp-panel-key') || '';

    this.selectPanel(initialKey, { dispatch: false });
    this.initialized = true;
    scheduleInitialServiceTabSync();
  }

  disconnectedCallback() {
    if (!this.initialized) {
      return;
    }

    if (this.dataset.tabMode === 'groups') {
      this.removeEventListener('click', this.handleGroupClick);
      this.removeEventListener('keydown', this.handleGroupKeydown);
      this.initialized = false;
      return;
    }

    this.removeEventListener('click', this.handleTabClick);
    this.removeEventListener('keydown', this.handleTabKeydown);
    document.removeEventListener('size-guide:category-change', this.handleCategoryChange);

    this.initialized = false;
  }

  handleNavigation() {
    this.tabsNav = this.querySelector('.service-page-tab__secondary');
    this.allTabs = this.querySelectorAll('.service-page-tab__secondary-item');
    this.currentTab = this.allTabs[0];

    const getAllTabsWidth = () => [...this.allTabs].reduce((width, tab) => width + tab.offsetWidth, 0);

    this.setNavigationButtonVisibility();

    this.querySelectorAll('button[data-prev], button[data-next]')
      .forEach(button => {
        button.addEventListener('click', () => {
          const isNextButton = button.hasAttribute('data-next'); 
          this.currentTab = (isNextButton ? this.currentTab.nextElementSibling : this.currentTab.previousElementSibling) || this.currentTab;

          this.scrollTabIntoView(this.currentTab.querySelector('button'));
          this.setNavigationButtonVisibility();
        });
      });

    const handleWindowResize = () => {
      if (window.innerWidth < getAllTabsWidth()) this.classList.add('show-navigation');
      else this.classList.remove('show-navigation');
    }

    handleWindowResize();

    window.addEventListener(
      'resize',
      theme.utils.debounce(100, handleWindowResize)
    );
  }

  setNavigationButtonVisibility() {
    const isFirstTab = this.currentTab === this.querySelector('.service-page-tab__secondary-item');
    const isLastTab = this.currentTab === [...this.querySelectorAll('.service-page-tab__secondary-item')].at(-1);
    this.querySelector('button[data-prev]').style.opacity = isFirstTab ? 0 : 1;
    this.querySelector('button[data-next]').style.opacity = isLastTab ? 0 : 1;
  }

  toggleBtnClass(tab) {
    this.querySelector('.service-page-tab__secondary-tab.btn').classList.remove('btn');
    tab.classList.add('btn');
  }

  /**
   * @param {CustomEvent} event
   */
  handleCategoryChange(event) {
    const { category, source } = event.detail || {};
    if (source === 'service-page-tabs') {
      return;
    }

    const normalised = String(category || '').trim().toLowerCase();
    if (!normalised) {
      return;
    }

    const tab = this.tabs.find((t) => {
      const key = (t.dataset.category || t.dataset.sgCategory || '').trim().toLowerCase();
      return key === normalised;
    });

    if (!tab) {
      return;
    }

    const panelKey = tab.getAttribute('data-sp-panel-key') || '';
    this.selectPanel(panelKey, { dispatch: false });
  }

  /**
   * @param {MouseEvent} event
   */
  handleTabClick(event) {
    const tab = event.target.closest('button[data-sp-panel-key]');
    if (!tab || !this.contains(tab)) {
      return;
    }

    if (this.hasAttribute('data-navigation')) {
      this.currentTab = tab.closest('.service-page-tab__secondary-item');
      this.setNavigationButtonVisibility();
      this.toggleBtnClass(tab);
    }

    const key = tab.getAttribute('data-sp-panel-key') || '';
    this.selectPanel(key);
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleTabKeydown(event) {
    const tab = event.target.closest('button[data-sp-panel-key]');
    if (!tab || !this.contains(tab)) {
      return;
    }

    const key = event.key;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Home' && key !== 'End') {
      return;
    }

    event.preventDefault();
    const list = this.tabs;
    const idx = list.indexOf(tab);
    if (idx === -1) {
      return;
    }

    let next = idx;
    if (key === 'ArrowLeft') {
      next = idx > 0 ? idx - 1 : list.length - 1;
    } else if (key === 'ArrowRight') {
      next = idx < list.length - 1 ? idx + 1 : 0;
    } else if (key === 'Home') {
      next = 0;
    } else if (key === 'End') {
      next = list.length - 1;
    }

    const target = list[next];
    if (target) {
      const nextKey = target.getAttribute('data-sp-panel-key') || '';
      target.focus();
      this.selectPanel(nextKey);
    }
  }

  /**
   * @param {string} panelKey
   * @param {{ dispatch?: boolean }} [options]
   */
  selectPanel(panelKey, options = {}) {
    const { dispatch = true } = options;
    const key = String(panelKey || '').trim();
    const hasMatch = this.panels.some((p) => p.getAttribute('data-sp-panel-key') === key);
    const resolvedKey = hasMatch ? key : (this.tabs[0]?.getAttribute('data-sp-panel-key') || '');

    this.state = { key: resolvedKey };
    this.syncDom();

    if (dispatch) {
      this.syncSizeGuideTable();
    }
  }

  /**
   * Keeps custom-size-table (source_key = data-guide-key) aligned with the selected tab.
   */
  syncSizeGuideTable() {
    const { key } = this.state;
    const tab = this.tabs.find((t) => (t.getAttribute('data-sp-panel-key') || '') === key);

    if (!tab) {
      return;
    }

    const category = (tab.dataset.category || tab.dataset.sgCategory || '').trim().toLowerCase();
    const title = (tab.dataset.title || tab.dataset.sgTitle || '').trim();

    if (!category) {
      return;
    }

    dispatchSizeGuideCategoryChange(category, title, 'service-page-tabs');
    syncSizeGuideDynamicTitle(title);
  }

  syncDom() {
    const { key } = this.state;

    this.tabs.forEach((tab) => {
      const tabKey = tab.getAttribute('data-sp-panel-key') || '';
      const isSel = tabKey === key;
      tab.setAttribute('aria-selected', isSel ? 'true' : 'false');
      tab.tabIndex = isSel ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      const panelKey = panel.getAttribute('data-sp-panel-key') || '';
      const match = panelKey === key;
      panel.hidden = !match;
      if (match) {
        panel.removeAttribute('aria-hidden');
      } else {
        panel.setAttribute('aria-hidden', 'true');
      }
    });

    const activeTab = this.tabs.find((t) => (t.getAttribute('data-sp-panel-key') || '') === key);
    if (activeTab) this.scrollTabIntoView(activeTab);
  }

  /* ---------- Groups mode (show/hide sibling sections by tab_group) ---------- */

  /**
   * Initializes groups mode: tabs toggle whole sibling sections marked with
   * data-tab-group. No size-guide:category-change is dispatched, so the charts-mode
   * sync, the carousel, and the size tables are never driven from here.
   */
  initGroupsMode() {
    this.groupTabs = [...this.querySelectorAll('button[data-sp-group][role="tab"]')];
    if (!this.groupTabs.length) {
      return;
    }

    // Cross-section lookup: the controlled sections are siblings, not descendants,
    // so document scope is required here (exception to the this-scoping rule).
    this.groupSections = [...document.querySelectorAll('[data-tab-group]')];

    // Hand control to JS: disables the server-rendered first-paint fallback styles.
    this.groupSections.forEach((sectionEl) => sectionEl.setAttribute('data-sg-ready', ''));

    this.addEventListener('click', this.handleGroupClick);
    this.addEventListener('keydown', this.handleGroupKeydown);

    const initialTab =
      this.groupTabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ||
      this.groupTabs[0];
    this.selectGroup(initialTab?.dataset.spGroup || '');

    this.initialized = true;
  }

  /**
   * @param {string} group
   * @returns {string}
   */
  normaliseGroup(group) {
    return String(group || '').trim().toLowerCase();
  }

  /**
   * Shows sections whose data-tab-group matches, hides the rest, and updates tab state.
   * @param {string} group
   */
  selectGroup(group) {
    const target = this.normaliseGroup(group);
    if (!target) {
      return;
    }

    this.groupSections.forEach((sectionEl) => {
      const match = this.normaliseGroup(sectionEl.dataset.tabGroup) === target;
      sectionEl.hidden = !match;
    });

    this.groupTabs.forEach((tab) => {
      const isSel = this.normaliseGroup(tab.dataset.spGroup) === target;
      tab.setAttribute('aria-selected', isSel ? 'true' : 'false');
      tab.tabIndex = isSel ? 0 : -1;
    });

    const activeGroupTab = this.groupTabs.find(
      (t) => this.normaliseGroup(t.dataset.spGroup) === target
    );
    if (activeGroupTab) this.scrollTabIntoView(activeGroupTab);
  }

  /**
   * Smoothly scrolls the nav container so the given tab is horizontally centred.
   * @param {HTMLElement} tab
   */
  scrollTabIntoView(tab) {
    const nav = tab.closest('.service-page-tab__secondary');
    if (!nav) return;
    const scrollTarget = tab.offsetLeft - nav.offsetWidth / 2 + tab.offsetWidth / 2;
    nav.scrollTo({ left: scrollTarget, behavior: 'smooth' });
  }

  /** @param {MouseEvent} event */
  handleGroupClick(event) {
    const tab = event.target.closest('button[data-sp-group]');
    if (!tab || !this.contains(tab)) {
      return;
    }
    this.selectGroup(tab.dataset.spGroup || '');
  }

  /** @param {KeyboardEvent} event */
  handleGroupKeydown(event) {
    const tab = event.target.closest('button[data-sp-group]');
    if (!tab || !this.contains(tab)) {
      return;
    }

    const { key } = event;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Home' && key !== 'End') {
      return;
    }

    event.preventDefault();
    const idx = this.groupTabs.indexOf(tab);
    if (idx === -1) {
      return;
    }

    let next = idx;
    if (key === 'ArrowLeft') {
      next = idx > 0 ? idx - 1 : this.groupTabs.length - 1;
    } else if (key === 'ArrowRight') {
      next = idx < this.groupTabs.length - 1 ? idx + 1 : 0;
    } else if (key === 'Home') {
      next = 0;
    } else if (key === 'End') {
      next = this.groupTabs.length - 1;
    }

    const targetTab = this.groupTabs[next];
    if (targetTab) {
      targetTab.focus();
      this.selectGroup(targetTab.dataset.spGroup || '');
    }
  }
}

if (!customElements.get('service-page-tabs')) {
  customElements.define('service-page-tabs', ServicePageTabs);
}

document.addEventListener('size-guide:category-change', (event) => {
  const { title, source } = event.detail || {};
  if (source !== 'service-page-tabs') {
    syncSizeGuideDynamicTitle(title);
  }
});

document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('service-page-tabs')) {
    syncFromActiveServiceTab();
  }
});
